
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { createLocalDateTime, getDefaultEndTime, slotSizeToDurationHours } from "@/utils/timeUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface BlockTimeDialogProps {
  serviceName: string;
  selectedDate: Date | undefined;
  selectedTimeSlot: string;
  isOpen: boolean;
  onClose: () => void;
  onBlock: (startTime: string, endTime: string) => Promise<void>;
  loading?: boolean;
  serviceId?: string | number;
}

export const BlockTimeDialog = ({ 
  serviceName, 
  selectedDate,
  selectedTimeSlot,
  isOpen,
  onClose,
  onBlock,
  loading = false,
  serviceId,
}: BlockTimeDialogProps) => {
  const [startTime, setStartTime] = useState(selectedTimeSlot);
  const [endTime, setEndTime] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [serviceSlotSize, setServiceSlotSize] = useState<string>("30");

  // Fetch service slot size when dialog opens
  useEffect(() => {
    const fetchServiceSlotSize = async () => {
      if (serviceId && isOpen) {
        try {
          const { data, error } = await supabase
            .from('services')
            .select('slot_size')
            .eq('id', serviceId)
            .single();
          
          if (!error && data?.slot_size) {
            setServiceSlotSize(data.slot_size);
          }
        } catch (error) {
          console.error('Error fetching service slot size:', error);
        }
      }
    };

    fetchServiceSlotSize();
  }, [serviceId, isOpen]);

  useEffect(() => {
    setStartTime(selectedTimeSlot);
    if (selectedTimeSlot && serviceSlotSize) {
      // Use service's slot size to calculate default end time
      const durationHours = slotSizeToDurationHours(serviceSlotSize);
      const calculatedEndTime = getDefaultEndTime(selectedTimeSlot, durationHours);
      setEndTime(calculatedEndTime);
    }
  }, [selectedTimeSlot, serviceSlotSize]);

  useEffect(() => {
    if (isAllDay) {
      setStartTime('07:00');
      setEndTime('19:00');
    }
  }, [isAllDay]);

  useEffect(() => {
    // Clear conflict error when time selection changes
    if (startTime && endTime && selectedDate && serviceId) {
      checkForTimeConflicts(startTime, endTime);
    } else {
      setConflictError(null);
    }
  }, [startTime, endTime, selectedDate, serviceId]);

  const checkForTimeConflicts = async (start: string, end: string) => {
    if (!selectedDate || !start || !end || !serviceId) return;
    
    try {
      // Simple local date/time approach - no timezone conversion
      const { date: startDateStr, time: startTimeStr } = createLocalDateTime(selectedDate, start);
      const { time: endTimeStr } = createLocalDateTime(selectedDate, end);
      
      console.log(`🔍 Block conflict check: ${startDateStr} ${startTimeStr} - ${endTimeStr} (local)`);
      
      // Simplified conflict check using only the new local time format
      // This avoids timezone confusion by sticking to local time strings
      const { data: conflictingBookings, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('service_id', typeof serviceId === 'string' ? parseInt(serviceId) : serviceId)
        .eq('booking_date', startDateStr)
        .or(`and(start_time_local.lt.${endTimeStr},end_time_local.gt.${startTimeStr})`);
      
      if (error) throw error;
      
      console.log('🔍 Conflict check results:', { 
        searchDate: startDateStr, 
        searchTime: `${startTimeStr} - ${endTimeStr}`,
        foundConflicts: conflictingBookings?.length || 0,
        conflicts: conflictingBookings?.map(b => ({
          id: b.id,
          date: b.booking_date,
          time: `${b.start_time_local} - ${b.end_time_local}`,
          status: b.status
        }))
      });
      
      if (conflictingBookings && conflictingBookings.length > 0) {
        setConflictError('This time slot conflicts with an existing booking or blocked time. Please select a different time.');
      } else {
        setConflictError(null);
      }
    } catch (error) {
      console.error('Error checking for time conflicts:', error);
      setConflictError(null);
    }
  };

  const timeSlots = [];
  for (let hour = 7; hour <= 19; hour++) {
    const formattedHour = hour.toString().padStart(2, '0');
    timeSlots.push(`${formattedHour}:00`);
    if (hour < 19) {
      timeSlots.push(`${formattedHour}:30`);
    }
  }

  const availableEndTimes = startTime 
    ? timeSlots.slice(timeSlots.indexOf(startTime) + 1)
    : [];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleBlock = () => {
    if (startTime && endTime && !conflictError) {
      onBlock(startTime, endTime);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Block Time - {serviceName}</DialogTitle>
          <DialogDescription>
            Are you sure you want to block this time slot? This will prevent customers from booking during this time.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-900">
              Time slot to block:
            </p>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="font-medium">{selectedDate && format(selectedDate, "MMMM d, yyyy")}</p>
              
              <div className="flex items-center space-x-2 mt-2 mb-4">
                <label className="text-sm font-medium text-gray-900 flex items-center space-x-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAllDay}
                    onChange={(e) => setIsAllDay(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>Block All Day (7:00 - 19:00)</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-900">Start Time</Label>
                  <Select 
                    value={startTime} 
                    onValueChange={setStartTime}
                    disabled={isAllDay}
                  >
                    <SelectTrigger className="w-full bg-white border-2 border-gray-300">
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent className="z-[100] bg-white">
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time} className="cursor-pointer">
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-900">End Time</Label>
                  <Select 
                    value={endTime} 
                    onValueChange={setEndTime}
                    disabled={isAllDay || !startTime}
                  >
                    <SelectTrigger className="w-full bg-white border-2 border-gray-300">
                      <SelectValue placeholder="Select end time" />
                    </SelectTrigger>
                    <SelectContent className="z-[100] bg-white">
                      {availableEndTimes.map((time) => (
                        <SelectItem key={time} value={time} className="cursor-pointer">
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {conflictError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  <AlertDescription>{conflictError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          <div>
            <Button 
              onClick={handleBlock}
              disabled={loading || !startTime || !endTime || !!conflictError}
              className="w-full"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {loading ? 'Blocking...' : 'Block Time Slot'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
